<template>
    <m-pageframe :title="uiPageTitle">
        <v-container fluid style="padding: 15px">
            <v-card-title>
                <v-btn icon @click="goBackToImportSearch" style="margin-right: 10px;" title="Back to Import Search">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-icon left>mdi-file-upload</v-icon>
                {{ uiPageTitle }}
            </v-card-title>

            <v-card-text>
                <!-- Staff real account warning -->
                <v-alert v-if="$rc.$state.isAuthActorSystemAdmin() && !$rc.$state.isDemoOrTestOrganization()"
                    type="warning" style="margin-bottom: 20px;">
                    <div><strong>Warning</strong></div>
                    <div>This is a real customer account. Changes made here will affect production data.</div>
                </v-alert>

                <!-- Read-Only Mode Alert -->
                <v-alert v-if="isReadOnlyMode && !$rc.$state.isAuthActorSystemAdmin()" type="info"
                    style="margin-bottom: 20px;">
                    <div><strong>Read-Only Access</strong></div>
                    <div>
                        You have read-only access to contact imports. Only organization administrators can create or
                        modify imports.
                    </div>
                </v-alert>

                <!-- Target Information Card -->
                <v-card v-if="computedTargetListName || computedTargetBulkMessageName" class="mb-4 elevation-2">
                    <v-card-title class="pb-2">
                        <v-icon v-if="computedTargetListName" color="purple"
                            class="mr-2">mdi-format-list-bulleted</v-icon>
                        <v-icon v-if="computedTargetBulkMessageName" color="blue"
                            class="mr-2">mdi-email-multiple</v-icon>
                        {{ computedTargetListName ? 'Target List' : 'Target Bulk Message' }}
                    </v-card-title>
                    <v-card-text class="pt-0">
                        <div class="text-h6">{{ computedTargetListName || computedTargetBulkMessageName }}</div>
                    </v-card-text>
                </v-card>

                <!-- Wizard Stepper -->
                <v-stepper v-model="current_step" alt-labels style="margin-bottom: 20px;">
                    <v-stepper-header>
                        <v-stepper-step :complete="current_step > 1" step="1" :editable="canEditStep(1)"
                            @click="goToStep(1)">
                            Upload File
                            <small v-if="import_meta_data">{{ import_meta_data.original_file_name }}</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 2" step="2" :editable="canEditStep(2)"
                            @click="goToStep(2)">
                            Column Mapping
                            <small>Map your columns</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 3" step="3" :editable="canEditStep(3)"
                            @click="goToStep(3)">
                            Review Preview
                            <small v-if="preview_data">{{ preview_data.total_records || 0 }} records</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step step="4" :editable="false">
                            Import Complete
                        </v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items>
                        <!-- Step 1: Upload File -->
                        <v-stepper-content step="1">
                            <div style="max-width: 500px; margin: 0 auto;" v-if="!initial_load_complete">
                                <div style="margin-bottom: 8px; font-weight: 500;">
                                    Loading Import...
                                </div>
                                <v-progress-linear indeterminate color="primary" height="12" rounded>
                                </v-progress-linear>
                                <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                    Please wait while we load your import.
                                </div>
                            </div>
                            <!-- Instructions and Upload Form (when not uploading) -->
                            <div v-else-if="!uploading">
                                <div style="margin-bottom: 20px;">
                                    <h3>Instructions</h3>
                                    <p>
                                        {{ uiInstructionsText }} If a record already exists in the system, the import
                                        will update the existing record rather than creating a new one.
                                    </p>

                                    <p>
                                        <strong>Important:</strong> There is a re-mapping step but the closer you can
                                        get your file to match the format
                                        below, the easier the import will be. Below is a template you can use to get
                                        started.
                                    </p>

                                    <div style="margin: 15px 0;">
                                        <v-btn color="secondary" v-on:click="onClickDownloadTemplate" outlined>
                                            <v-icon left>mdi-download</v-icon>
                                            Download Template File
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Field Requirements Section -->
                                <div style="margin-bottom: 20px;">
                                    <h3 style="margin-bottom: 10px;">
                                        Excel File Format Requirements
                                    </h3>
                                    <p style="margin-bottom: 15px; color: #666;">
                                        Your Excel file must contain columns with data for all the required columns
                                        below. Optional fields can be included to provide additional contact
                                        information.
                                    </p>

                                    <v-card outlined>
                                        <v-simple-table>
                                            <template v-slot:default>
                                                <thead>
                                                    <tr style="background-color: #f5f5f5;">
                                                        <th class="text-left" style="font-weight: 600;">Column Name</th>
                                                        <th class="text-left" style="font-weight: 600;">Required</th>
                                                        <th class="text-left" style="font-weight: 600;">Format</th>
                                                        <th class="text-left" style="font-weight: 600;">Example</th>
                                                        <th class="text-left" style="font-weight: 600;">Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Phone Number</strong></td>
                                                        <td>
                                                            <v-chip small color="red"
                                                                text-color="white">Required</v-chip>
                                                        </td>
                                                        <td>Phone format</td>
                                                        <td>555-123-4567<br />(555) 123-4567</td>
                                                        <td>Primary phone number for the contact</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>First Name</strong></td>
                                                        <td>
                                                            <v-tooltip top>
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-chip small color="red" text-color="white"
                                                                        v-bind="attrs" v-on="on">Required &nbsp;<v-icon
                                                                            small>mdi-information-outline</v-icon></v-chip>
                                                                </template>
                                                                <span>Selecting a first name column will be required;
                                                                    however, if a row is missing a first name it will
                                                                    still be
                                                                    accepted.</span>
                                                            </v-tooltip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>John</td>
                                                        <td>Contact's first name</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Last Name</strong></td>
                                                        <td>
                                                            <v-tooltip top>
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-chip small color="red" text-color="white"
                                                                        v-bind="attrs" v-on="on">Required &nbsp;<v-icon
                                                                            small>mdi-information-outline</v-icon></v-chip>
                                                                </template>
                                                                <span>Selecting a last name column will be required;
                                                                    however, if a row is missing a last name it will
                                                                    still
                                                                    be accepted.</span>
                                                            </v-tooltip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>Doe</td>
                                                        <td>Contact's last name</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Email</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Email format</td>
                                                        <td>john.doe@example.com</td>
                                                        <td>Contact's email address</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Birthdate</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Date format</td>
                                                        <td>12/25/1990<br />1990-12-25</td>
                                                        <td>Contact's birthdate (supports multiple date formats)</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Notes</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>Imported contact</td>
                                                        <td>Additional notes about the contact</td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </v-card>

                                </div>

                                <v-divider style="margin: 20px 0;"></v-divider>

                                <!-- File Upload Section -->
                                <div v-if="hasWriteAccess && (!import_id || import_state === 'uploaded')">
                                    <h4>Upload Excel File</h4>

                                    <v-file-input v-model="excel_file" show-size accept=".xlsx"
                                        label="Excel File (XLSX)" style="padding-bottom: 15px; max-width: 500px;"
                                        :rules="file_rules" :disabled="uploading" />

                                    <div style="margin-bottom: 20px;">
                                        <v-btn color="primary" v-on:click="onClickUploadFile"
                                            :disabled="!can_upload_file" :loading="uploading">
                                            <v-icon left>mdi-upload</v-icon>
                                            Upload File
                                        </v-btn>
                                    </div>
                                    <div v-if="upload_errors.length > 0">
                                        <v-alert type="error" style="margin-bottom: 15px;">
                                            <div><strong>Upload Errors:</strong></div>
                                            <ul>
                                                <li v-for="error in upload_errors" :key="error">{{ error }}</li>
                                            </ul>
                                        </v-alert>
                                    </div>
                                </div>
                            </div>

                            <!-- Upload Progress -->
                            <ImportProcessProgress v-if="uploading" title="Uploading File..."
                                :description="uiUploadProgressDescription" :process-id="upload_process_id"
                                :import-type="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Uploading your file..." @complete="onUploadComplete"
                                @failed="onUploadFailed" @progress="updateUploadProgress" />

                            <!-- Upload Failed -->
                            <div v-else-if="intermediate_process_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;"
                                    v-if="intermediate_process_errors.length > 0">
                                    <div v-for="error in intermediate_process_errors" :key="error">
                                        {{ error }}. Please try again.
                                    </div>
                                </v-alert>
                                <v-alert type="error" style="margin-bottom: 20px;" v-else>
                                    Unable to load sheet information. Please try uploading the file again.
                                </v-alert>
                                <v-btn color="secondary" v-on:click="startNewImport">
                                    <v-icon left>mdi-upload</v-icon>
                                    Start New Upload
                                </v-btn>
                            </div>

                        </v-stepper-content>

                        <!-- Step 2: Column Mapping -->
                        <v-stepper-content step="2">
                            <div v-if="sheet_information">
                                <ColumnMappingComponent :available-sheets="sheet_information.available_sheets"
                                    :required-fields="computedRequiredFields" :optional-fields="computedOptionalFields"
                                    :importType="importType" :suggested-mapping="suggestedMapping"
                                    :sample-data="sheet_information.available_sheets" :saving="saving_column_mapping"
                                    @mapping-changed="onColumnMappingChanged"
                                    @can-continue-changed="onCanContinueChanged"
                                    @continue-to-preview="onContinueToPreview" />
                            </div>
                            <div v-else-if="loading_sheet_information" style="text-align: center; padding: 40px 0;">
                                <h3 style="margin-bottom: 20px;">
                                    Loading Sheet Information...
                                </h3>

                                <!-- Loading Progress -->
                                <div style="max-width: 500px; margin: 0 auto;">
                                    <div style="margin-bottom: 8px; font-weight: 500;">
                                        Analyzing your Excel file structure
                                    </div>
                                    <v-progress-linear indeterminate color="primary" height="12" rounded>
                                    </v-progress-linear>
                                    <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                        Please wait while we prepare the column mapping
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <v-alert type="error" style="margin-bottom: 20px;"
                                    v-if="intermediate_process_errors.length > 0">
                                    <div v-for="error in intermediate_process_errors" :key="error">
                                        {{ error }}. Please try again.
                                    </div>
                                    <v-btn color="secondary" v-on:click="startNewImport">
                                        <v-icon left>mdi-upload</v-icon>
                                        Start New Upload
                                    </v-btn>
                                </v-alert>
                                <v-alert type="error" style="margin-bottom: 20px;" v-else>
                                    Unable to load sheet information. Please try uploading the file again.
                                    <v-btn color="secondary" v-on:click="startNewImport">
                                        <v-icon left>mdi-upload</v-icon>
                                        Start New Upload
                                    </v-btn>
                                </v-alert>

                            </div>
                            <ImportProcessProgress v-if="saving_column_mapping && intermediate_process_id"
                                title="Creating Preview..."
                                description="Please wait while we process your column mapping and create a preview."
                                :process-id="intermediate_process_id" :importType="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Processing column mapping..." @complete="onIntermediateComplete"
                                @failed="onIntermediateFailed" />


                            <!-- Step 2 Navigation -->

                            <v-card-actions v-if="!loading_sheet_information" style="padding: 0; margin-top: 20px;">
                                <v-spacer></v-spacer>

                                <v-btn color="primary" @click="onContinueToPreview"
                                    :disabled="!canContinueToPreview || saving_column_mapping"
                                    :loading="saving_column_mapping">
                                    Continue to Preview
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </v-stepper-content>

                        <!-- Step 3: Review Preview -->
                        <v-stepper-content step="3">

                            <div style="max-width: 500px; margin: 0 auto;" v-if="generating_preview">
                                <div style="margin-bottom: 8px; font-weight: 500;">
                                    Generating Preview...
                                </div>
                                <v-progress-linear indeterminate color="primary" height="12" rounded>
                                </v-progress-linear>
                                <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                    Please wait while we prepare your preview
                                </div>
                            </div>

                            <div v-else-if="preview_failed">
                                <v-alert type="error" prominent border="left" class="mb-4">
                                    <div class="text-h6">Preview Generation Failed</div>
                                    <div>{{ preview_error_message }}</div>
                                </v-alert>
                                <v-btn color="primary" @click="startPreviewGeneration" :loading="generating_preview">
                                    <v-icon left>mdi-refresh</v-icon>
                                    Retry Preview
                                </v-btn>
                            </div>

                            <div v-else-if="preview_ready">
                                <v-alert v-if="import_state === 'cancelled' || import_state === 'failed'"
                                    :type="import_state === 'failed' ? 'error' : 'warning'" prominent border="left">

                                    <div style="display: flex; align-items: center; justify-content: space-between;">
                                        <div>
                                            <div><strong>{{ import_state === 'failed' ? 'Import Failed' : 'Import Cancelled' }}</strong></div>
                                            <div>This import has been cancelled and cannot be continued. You can
                                                view the preview below but
                                                cannot proceed to execution.</div>
                                        </div>
                                        <v-btn color="white" outlined style="margin-left: 20px;"
                                            v-on:click="startNewImport">
                                            <v-icon left>mdi-upload</v-icon>
                                            Start New Upload
                                        </v-btn>
                                    </div>
                                </v-alert>

                                <v-expansion-panels v-model="expansion_panel" v-if="import_state !== 'cancelled' && import_state !== 'failed'" class="mb-4">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header color="grey lighten-4">
                                            <template v-slot:default="">
                                                <v-row no-gutters class="align-center">
                                                    <v-col cols="auto">
                                                        <v-icon color="grey darken-2"
                                                            class="mr-4">mdi-alert-circle-outline</v-icon>
                                                    </v-col>
                                                    <v-col>
                                                        <div class="text-subtitle-1 font-weight-medium">Please review the below before executing</div>
                                                    </v-col>
                                                </v-row>
                                            </template>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content class="pt-4">
                                            <v-list dense>
                                                <v-list-item class="px-0">
                                                    <v-list-item-icon class="mr-4">
                                                        <v-icon color="success">mdi-account-plus</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>
                                                            <span class="font-weight-medium">New contacts</span> will be
                                                            created if no phone
                                                            number
                                                            match is found.
                                                        </v-list-item-title>
                                                        <v-list-item-subtitle>
                                                            See "System Notes" for details.
                                                        </v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item class="px-0">
                                                    <v-list-item-icon class="mr-4">
                                                        <v-icon color="info">mdi-account-edit</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>
                                                            <span class="font-weight-medium">Existing contacts</span>
                                                            will be
                                                            updated only if data has changed.
                                                        </v-list-item-title>
                                                        <v-list-item-subtitle>
                                                            See "System Notes" for a list of changes.
                                                        </v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item class="px-0">
                                                    <v-list-item-icon class="mr-4">
                                                        <v-icon color="warning">mdi-cancel</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>
                                                            <span class="font-weight-medium">Rows with errors</span>
                                                            will be
                                                            skipped.
                                                        </v-list-item-title>
                                                        <v-list-item-subtitle>
                                                            Refer to the "Errors" column for specific reasons.
                                                        </v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item v-if="importTypeSpecificMessage" class="px-0">
                                                    <v-list-item-icon class="mr-4">
                                                        <v-icon :color="importTypeSpecificMessage.color">{{
                                                            importTypeSpecificMessage.icon }}</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title class="font-weight-medium">
                                                            {{ importTypeSpecificMessage.title }}
                                                        </v-list-item-title>
                                                        <v-list-item-subtitle>
                                                            {{ importTypeSpecificMessage.subtitle }}
                                                        </v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </v-list-item>

                                            </v-list>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <v-alert color="warning"  text v-if="above_warn_change_contact_threshold"
                                    class="mb-4">
                                    <div v-html="above_contact_threshold_warning_message"></div>
                                </v-alert>
                                <v-alert type="info"  text v-if="isCanadianOrganization && used_phone_validation"
                                    class="mb-4">
                                    For Canadian phone numbers, text message eligibility cannot be verified in advance. The number will be treated as valid, but the first message may be rejected by the carrier if the number cannot receive texts. 
                                </v-alert>

                                <v-card outlined class="mb-4">
                                    <v-card-text class="py-2 d-flex align-center">
                                        <v-icon class="ml-1 mr-3">mdi-file-excel</v-icon>
                                        <div class="text-subtitle-1 font-weight-medium">
                                            Selected Sheet: <span>'{{ sheetName || 'Not specified' }}'</span>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <v-btn small text color="primary"
                                            v-if="import_meta_data && import_meta_data.enriched_data_blob_id"
                                            @click="onClickDownloadEnrichedDataFromPreview">
                                            <v-icon left small>mdi-download</v-icon>
                                            Download Mapped File
                                        </v-btn>
                                    </v-card-text>
                                </v-card>

                                <ContactImportPreviewTable v-if="preview_data && !generating_preview && !preview_failed"
                                    :preview-data="preview_data" :show-summary="true"
                                    :summary-cards="uiContactSummaryCards" :custom-columns="uiContactCustomColumns"
                                    :table-headers="uiContactTableHeaders" :items-per-page="25" :hide-footer="false"
                                    :loading="false" :auto-filter-to-errors="false" :selected-rows="selected_rows"
                                    :select-all="select_all" @toggle-row-selection="toggleRowSelection"
                                    @toggle-select-all="toggleSelectAll" />
                            </div>

                            <v-card-actions v-if="preview_ready && !generating_preview && !importing" class="mt-4 pa-0">

                                <v-menu offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn color="secondary" class="ml-2" v-bind="attrs" v-on="on"
                                            :loading="validating_phone_numbers"
                                            :disabled="validating_phone_numbers || !hasWriteAccess || !canCancelImport">
                                            <v-icon left>mdi-phone-check</v-icon>
                                            Phone Options
                                            <v-icon right>mdi-menu-down</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list>
                                        <v-list-item @click="validateUnknownPhoneNumbers">
                                            <v-list-item-icon><v-icon>mdi-check-decagram</v-icon></v-list-item-icon>
                                            <v-list-item-title>Validate Unknown Numbers</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="allowInvalidPhoneNumbers">
                                            <v-list-item-icon><v-icon>mdi-alert-circle-outline</v-icon></v-list-item-icon>
                                            <v-list-item-title>Allow Invalid Numbers</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                    
                                </v-menu>
                                <span v-if="phone_validation_status"
                                    style="color: #666; font-size: 14px; margin-left: 15px;">
                                    {{ phone_validation_status }}
                                </span>
                                                                
                                <v-spacer></v-spacer>

                                <v-btn color="error" outlined @click="cancelImport" class="mr-2"
                                    v-if="canCancelImport && hasWriteAccess">
                                    <v-icon left>mdi-cancel</v-icon> Cancel
                                </v-btn>

                                <v-tooltip bottom :disabled="canProceedToExecution">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on">
                                            <v-btn color="primary" :disabled="!canProceedToExecution"
                                                @click="executeImport" :loading="importing">
                                                <v-icon left>mdi-play</v-icon> Execute Import
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>{{ executionDisabledReason }}</span>
                                </v-tooltip>
                            </v-card-actions>
                        </v-stepper-content>

                        <!-- Step 4: Import Complete -->
                        <v-stepper-content step="4">
                            <!-- Import Execution Progress -->
                            <ImportProcessProgress v-if="importing && import_process_id"
                                :title="uiExecutionProgressTitle" :description="uiExecutionProgressDescription"
                                :process-id="import_process_id" :import-type="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                :default-status-message="uiExecutionStatusMessage" @complete="onExecutionComplete"
                                @failed="onExecutionFailed" @progress="updateExecutionProgress" />
                            <div v-else-if="execution_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;">
                                    <div><strong>Import Failed Unexpectedly</strong></div>
                                    <div v-for="error in execution_errors" :key="error">{{ error }}</div>
                                </v-alert>
                                <v-btn color="secondary" v-on:click="startNewImport">
                                    <v-icon left>mdi-upload</v-icon>
                                    Start New Upload
                                </v-btn>
                            </div>

                            <!-- Import Complete -->
                            <div v-else-if="!importing" style="text-align: center; padding: 40px 0;">
                                <v-icon color="success" size="80">mdi-check-circle</v-icon>

                                <h3 class="success--text" style="margin-top: 20px;">
                                    {{ uiCompletionTitle }}
                                </h3>

                                <p style="margin-top: 10px; color: #666;">
                                    {{ uiCompletionDescription }}
                                </p>

                                <!-- Navigation Options -->
                                <div style="margin-top: 30px;">
                                    <h4 style="margin-bottom: 20px; color: #333;">What would you like to do next?
                                    </h4>
                                    <div
                                        style="display: flex; flex-direction: column; gap: 15px; max-width: 600px; margin: 0 auto;">

                                        <!-- Primary Action Button (context-specific) -->
                                        <v-btn color="primary" large @click="goToPrimaryDestination"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>{{ (primaryDestination && primaryDestination.icon) ||
                                                'mdi-account-search' }}</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">{{ (primaryDestination &&
                                                    primaryDestination.title) || 'Open Destination' }}</div>
                                                <div style="font-size: 12px; opacity: 0.8;">{{
                                                    (primaryDestination && primaryDestination.description) || 'Open the primary destination for this import' }}</div>
                                            </div>
                                        </v-btn>

                                        <!-- Go to Contact Import History -->
                                        <v-btn color="secondary" large outlined @click="goToImportHistory"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-history</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">View Import History</div>
                                                <div style="font-size: 12px; opacity: 0.8;">See all imports for this
                                                    organization</div>
                                            </div>
                                        </v-btn>

                                        <!-- Import More Contacts -->
                                        <v-btn color="grey darken-1" large outlined @click="startNewImport"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-plus</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">Import More</div>
                                                <div style="font-size: 12px; opacity: 0.8;">Start a new import</div>
                                            </div>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-stepper-content>

                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
        </v-container>
    </m-pageframe>
</template>

<script>
import ContactImportPreviewTable from '@/components/contacts/contact-import-preview-table.vue';
import ColumnMappingComponent from '@/components/common/column-mapping-component.vue';
import ImportProcessProgress from '@/components/common/import-process-progress.vue';
import ConsolidatedImportMixin from '@/mixins/consolidated-import-mixin.js';

export default {
    mixins: [ConsolidatedImportMixin],

    props: {
        // Allow parent components to override the import type
        overrideImportType: {
            type: String,
            default: null
        },
        // Customization props to tailor component for contact vs contact-list vs recipient
        pageCopy: {
            type: Object,
            default: () => ({})
        },
        fieldDefinitions: {
            type: Object,
            default: () => ({})
        },
        previewConfig: {
            type: Object,
            default: () => ({})
        },
        // Target information props
        targetListName: {
            type: String,
            default: null
        },
        targetBulkMessageName: {
            type: String,
            default: null
        },

    },

    components: {
        ContactImportPreviewTable,
        ColumnMappingComponent,
        ImportProcessProgress
    },

    data() {
        return {
            expansion_panel: 0,

            // Contact-specific data (common data is provided by ConsolidatedImportMixin)
            // Validation rules
            file_rules: [
                v => !!v || 'File is required',
                v => !v || v.size < 50000000 || `File size should be less than 50 MB`,
                v => !v || v.name.toLowerCase().endsWith('.xlsx') || 'File must be Excel format (.xlsx)'
            ],

            // Contact-specific field definitions
            requiredFields: [
                {
                    key: 'phone_number',
                    label: 'Phone Number',
                    description: 'Phone number(s) for the contact - you can select multiple columns',
                    multiple: true,
                    required: true,
                    hint: 'Select one or more columns containing phone numbers'
                },
                {
                    key: 'first_name',
                    label: 'First Name',
                    description: 'Contact\'s first name',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing first names'
                },
                {
                    key: 'last_name',
                    label: 'Last Name',
                    description: 'Contact\'s last name',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing last names'
                }
            ],
            optionalFields: [
                { key: 'email', label: 'Email', description: 'Contact\'s email address', multiple: false, required: false, hint: 'Select the column containing email addresses' },
                { key: 'birthdate', label: 'Birthdate', description: 'Contact\'s birthdate', multiple: false, required: false, hint: 'Select the column containing birthdates (supports MM/DD/YYYY, MM/DD/YY, or YYYY-MM-DD formats)' },
                { key: 'notes', label: 'Notes', description: 'Contact\'s Notes', multiple: false, required: false, hint: 'Select the column containing notes about the contact' },
            ],

        }
    },

    computed: {
        isCanadianOrganization() {
            return this.$rc.$state?.profile?.organization_account?.country_and_subdivision?.startsWith('CA');
        },
        // Required by ConsolidatedImportMixin - Backend import type
        importType() {
            // If parent component provides an override, use it
            if (this.overrideImportType) {
                return this.overrideImportType;
            }
            return 'contact';
        },
        // Allow props to override field definitions
        computedRequiredFields() {
            return (this.fieldDefinitions && this.fieldDefinitions.requiredFields) ? this.fieldDefinitions.requiredFields : this.requiredFields;
        },
        computedOptionalFields() {
            return (this.fieldDefinitions && this.fieldDefinitions.optionalFields) ? this.fieldDefinitions.optionalFields : this.optionalFields;
        },
        // Prop-driven overrides for page copy
        uiPageTitle() {
            return (this.pageCopy && this.pageCopy.pageTitle) ? this.pageCopy.pageTitle : this.pageTitle;
        },
        uiInstructionsText() {
            return (this.pageCopy && this.pageCopy.instructionsText) ? this.pageCopy.instructionsText : this.instructionsText;
        },
        uiPreviewTableTitle() {
            return (this.previewConfig && this.previewConfig.tableTitle) ? this.previewConfig.tableTitle : this.previewTableTitle;
        },

        // Extract target names from import metadata or use props
        computedTargetListName() {
            // First check import metadata (most reliable source)
            if (this.import_meta_data && this.import_meta_data.target_list_names) {
                return this.import_meta_data.target_list_names;
            }
            // Fallback to prop if provided
            if (this.targetListName) {
                return this.targetListName;
            }
            return null;
        },

        computedTargetBulkMessageName() {
            // First check import metadata (most reliable source)
            if (this.import_meta_data && this.import_meta_data.target_bulk_message_names) {
                return this.import_meta_data.target_bulk_message_names;
            }
            // Fallback to prop if provided
            if (this.targetBulkMessageName) {
                return this.targetBulkMessageName;
            }
            return null;
        },
        // Preview UI customization via props
        uiContactSummaryCards() {
            return (this.previewConfig && this.previewConfig.summaryCards) ? this.previewConfig.summaryCards : this.contactSummaryCards;
        },
        uiContactCustomColumns() {
            return (this.previewConfig && this.previewConfig.customColumns) ? this.previewConfig.customColumns : this.contactCustomColumns;
        },
        uiContactTableHeaders() {
            return this.contactTableHeaders;
        },

        // Progress/copy overrides
        uiUploadProgressDescription() {
            return (this.pageCopy && this.pageCopy.uploadProgressDescription) ? this.pageCopy.uploadProgressDescription : this.uploadProgressDescription;
        },
        uiExecutionProgressTitle() {
            return (this.pageCopy && this.pageCopy.executionProgressTitle) ? this.pageCopy.executionProgressTitle : this.executionProgressTitle;
        },
        uiExecutionProgressDescription() {
            return (this.pageCopy && this.pageCopy.executionProgressDescription) ? this.pageCopy.executionProgressDescription : this.executionProgressDescription;
        },
        uiExecutionStatusMessage() {
            return (this.pageCopy && this.pageCopy.executionStatusMessage) ? this.pageCopy.executionStatusMessage : this.executionStatusMessage;
        },
        uiCompletionTitle() {
            return (this.pageCopy && this.pageCopy.completionTitle) ? this.pageCopy.completionTitle : this.completionTitle;
        },
        uiCompletionDescription() {
            return (this.pageCopy && this.pageCopy.completionDescription) ? this.pageCopy.completionDescription : this.completionDescription;
        },


        // Check if user can upload file
        can_upload_file() {
            return this.excel_file && !this.uploading;
        },

        // Check if user can proceed to column mapping
        canProceedToColumnMapping() {
            return this.import_state === 'uploaded' && this.sheet_information;
        },



        // Check if user can proceed to execution
        canProceedToExecution() {
            const nonExecutableStates = ['cancelled', 'completed', 'failed'];

            // Basic checks
            if (!this.preview_data || this.importing || this.isReadOnlyMode || nonExecutableStates.includes(this.import_state)) {
                return false;
            }

            // Check if there are any selected rows
            if (!this.selected_rows || this.selected_rows.length === 0) {
                return false;
            }

            // Check if all selected rows are valid for import (have will_be_imported = true)
            const validSelectedRows = this.selected_rows.filter(row => row.will_be_imported);
            if (validSelectedRows.length === 0) {
                return false;
            }

            return true;
        },

        // Get execution disabled reason for user feedback
        executionDisabledReason() {
            const nonExecutableStates = ['cancelled', 'completed', 'failed'];

            if (!this.preview_data) {
                return 'No preview data available';
            }

            if (this.importing) {
                return 'Import is currently running';
            }

            if (this.isReadOnlyMode) {
                return 'Read-only access';
            }

            if (nonExecutableStates.includes(this.import_state)) {
                return `Import is ${this.import_state}`;
            }

            if (!this.selected_rows || this.selected_rows.length === 0) {
                return 'No rows selected for import';
            }

            const validSelectedRows = this.selected_rows.filter(row => row.will_be_imported);
            if (validSelectedRows.length === 0) {
                return 'All selected rows have validation errors';
            }

            return '';
        },

        // Override ConsolidatedImportMixin - Contact imports have different cancellation rules
        canCancelImport() {
            const nonCancellableStates = ['cancelled', 'completed', 'failed'];
            return !nonCancellableStates.includes(this.import_state);
        },

        // Import type specific message for the fourth column
        importTypeSpecificMessage() {
            if (this.importType === 'bulk-message-recipients') {
                return {
                    icon: 'mdi-email-multiple',
                    color: 'purple',
                    title: 'Recipients that are not currently in the selected bulk message will be added to the recipient list.',
                    subtitle: 'Any identified existing recipients will only have their contact information updated.'
                };
            } else if (this.importType === 'contact-list') {
                return {
                    icon: 'mdi-format-list-bulleted',
                    color: 'blue',
                    title: 'Members that are not currently in the selected list will be added to list.',
                    subtitle:  'Any identified existing members will only have their contact information updated.'
                };
            }
            return null;
        },

        // Primary destination info: prefer explicit query params, then import metadata destinations, else contact search
        primaryDestination() {
            // 1) From explicit query (new imports started from a specific destination)
            if (this.$route.query.bulk_message_id) {
                return {
                    icon: 'mdi-email-multiple',
                    title: 'Go to Bulk Message',
                    description: 'Return to the bulk message that these contacts were added to',
                    navigate: () => this.$router.push({
                        name: 'view-bulk-message',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            bulk_message_id: this.$route.query.bulk_message_id
                        }
                    })
                };
            }
            if (this.$route.query.list_id) {
                return {
                    icon: 'mdi-format-list-bulleted',
                    title: 'Go to List',
                    description: 'Return to the list that these contacts were added to',
                    navigate: () => this.$router.push({
                        name: 'edit-list',
                        params: { list_id: this.$route.query.list_id }
                    })
                };
            }

            // 2) From import metadata when viewing an existing import from search (no query params)
            const add_to = this.import_meta_data && this.import_meta_data.add_import_result_to ? this.import_meta_data.add_import_result_to : null;
            const bulk_ids = add_to && Array.isArray(add_to.bulk_message_ids) && add_to.bulk_message_ids.length > 0 ? add_to.bulk_message_ids : null;
            if (bulk_ids) {
                const bulk_message_id = bulk_ids[0];
                return {
                    icon: 'mdi-email-multiple',
                    title: 'Go to Bulk Message',
                    description: 'Open the bulk message for this import',
                    navigate: () => this.$router.push({
                        name: 'view-bulk-message',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            bulk_message_id
                        }
                    })
                };
            }

            const list_ids = add_to && Array.isArray(add_to.list_ids) && add_to.list_ids.length > 0 ? add_to.list_ids : null;
            if (list_ids) {
                const list_id = list_ids[0];
                return {
                    icon: 'mdi-format-list-bulleted',
                    title: 'Go to List',
                    description: 'Open the contact list for this import',
                    navigate: () => this.$router.push({
                        name: 'edit-list',
                        params: { list_id }
                    })
                };
            }

            // 3) Fallback to contact search
            return {
                icon: 'mdi-account-search',
                title: 'Go to Contact Search',
                description: 'Search and manage all contacts in this organization',
                navigate: () => this.$router.push({
                    name: 'fulltext-search-contacts',
                    params: { organization_id: this.$route.params.organization_id }
                })
            };
        },

        // Check if user can proceed to preview
        canProceedToPreview() {
            const allowedStates = ['uploaded', 'preview-ready', 'failed', 'cancelled'];
            return this.import_id && (allowedStates.includes(this.import_state) || this.preview_ready);
        },

        // Check if user can edit a step
        canEditStep() {
            return (step) => {
                if (!this.hasWriteAccess) return false;
                if (this.uploading || this.generating_preview || this.importing) return false;

                const isCompleted = this.import_state === 'completed';

                switch (step) {
                    case 1:
                        return false; // Can't go back to upload once started
                    case 2:
                        // Can only edit column mapping if we're in uploaded state and haven't proceeded to preview yet
                        return this.import_state === 'uploaded' && this.sheet_information && this.current_step <= 2;
                    case 3:
                        return this.canContinueToPreview && !isCompleted;
                    case 4:
                        return isCompleted;
                    default:
                        return false;
                }
            };
        },

        // Instructions text based on import type
        instructionsText() {
            // Generic fallback; parents pass pageCopy.instructionsText for specific flows
            return 'Use this tool to import records from an Excel file.';
        },

        // Entity ID for the import (list_id or bulk_message_id)
        entityId() {
            return this.$route.query.list_id || this.$route.query.bulk_message_id || null;
        },

        // Dynamic page title based on import type
        pageTitle() {
            // Generic fallback; parents pass pageCopy.pageTitle for specific flows
            return 'Import Contacts';
        },

        canContinueToPreview() {
            return this.can_continue_to_preview && this.column_mapping_data && !this.saving_column_mapping;
        },


        uploadProgressDescription() {
            return 'Please wait while we upload and process your file.';
        },

        // Execution progress messages
        executionProgressTitle() {
            return 'Importing...';
        },

        executionProgressDescription() {
            return 'Please wait while we import your data.';
        },

        executionStatusMessage() {
            return 'Importing...';
        },

        // Completion messages
        completionTitle() {
            return 'Import Complete!';
        },

        completionDescription() {
            return 'Your import has completed successfully.';
        },

        contactSummaryCards() {
            if (!this.preview_data) {
                return [];
            }

            const cards = [];

            // Always show total records
            if (this.preview_data.total_records >= 0) {
                cards.push({ key: 'total_records', label: 'Total Records', cols: 3 });
            }

            // Show valid records count, always show
            if (this.preview_data.valid_records_count >= 0) {
                cards.push({ key: 'valid_records_count', label: 'Valid Records', cols: 3 });
            }

            // Show contacts to create if > 0
            if (this.preview_data.total_contacts_to_create > 0) {
                cards.push({ key: 'total_contacts_to_create', label: 'Contacts Available to Create', cols: 3 });
            }

            // Show contacts to update if > 0
            if (this.preview_data.total_contacts_to_update > 0) {
                cards.push({ key: 'total_contacts_to_update', label: 'Contacts Available to Update', cols: 3 });
            }

            // Show list members to add if > 0
            if (this.preview_data.total_list_members_to_add > 0) {
                cards.push({ key: 'total_list_members_to_add', label: 'List Members Available to Add', cols: 3 });
            }

            // Show bulk message recipients if > 0
            if (this.preview_data.total_bulk_message_recipients_to_add > 0) {
                cards.push({ key: 'total_bulk_message_recipients_to_add', label: 'Bulk Message Recipients Available to Add', cols: 3 });
            }

            // Show errors if any
            if (this.preview_data.error_records > 0) {
                cards.push({ key: 'error_records', label: 'Errors', cols: 3 });
            }

            // Show warnings if any
            if (this.preview_data.warning_records > 0) {
                cards.push({ key: 'warning_records', label: 'Warnings', cols: 3 });
            }

            return cards;
        },

        contactCustomColumns() {
            // Generic default; parents provide columns via previewConfig
            return [
                { key: 'select', type: 'custom' }
            ];
        },

        // Warning logic for large number of contact updates
        above_warn_change_contact_threshold() {
            if (!this.preview_data || !this.preview_data.total_contacts_to_update) {
                return false;
            }

            const total_contacts_to_update = this.preview_data.total_contacts_to_update;
            const valid_records = this.preview_data.valid_records_count || 0;

            // Show warning if more than 10 contacts would be updated AND it's more than 50% of valid records
            return total_contacts_to_update > 10 && valid_records > 0 && (total_contacts_to_update / valid_records) > 0.5;
        },

        above_contact_threshold_warning_message() {
            return 'Warning: This import would <b><u>change</u></b> the data of a large number of <b><u>pre-existing contacts</u></b>.  The data of more than <b><u>' + this.pretty_percent_updated_contacts + '</u></b> would be altered. This frequently happens when the imported file contains an error.  For example, the names and phone numbers are mis-aligned in your spreadsheet (even by one row).  Please check that your import does not have a problem <b><u>very carefully</u></b> before proceeding.';
        },

        pretty_percent_updated_contacts() {
            if (!this.preview_data || !this.preview_data.total_contacts_to_update || !this.preview_data.valid_records_count) {
                return '0%';
            }

            const percentage = (this.preview_data.total_contacts_to_update / this.preview_data.valid_records_count) * 100;
            return Math.round(percentage) + '%';
        },

        contactTableHeaders() {
            // Filter columns to only show those with data or core fields
            return this.previewConfig.tableHeaders.filter(column => {
                if (column.alwaysShow) return true;
                return this.hasDataForColumn(column.value);
            });
        },

    },

    mounted() {
        // Use ConsolidatedImportMixin initialization
        this.initializeComponent();
    },

    methods: {
        // Override ConsolidatedImportMixin to add query parameters to form data
        addAdditionalUploadData(form_data) {
            if (this.$route.query.list_id) {
                form_data.append('list_id', this.$route.query.list_id);
            }
            if (this.$route.query.bulk_message_id) {
                form_data.append('bulk_message_id', this.$route.query.bulk_message_id);
            }
        },
        // Check if any row has data for the given column
        hasDataForColumn(columnValue) {
            if (!this.preview_data || !this.preview_data.preview_rows) {
                return false;
            }

            return this.preview_data.preview_rows.some(row => {
                const value = row[columnValue];
                return value !== null && value !== undefined && value !== '';
            });
        },

        // Override goToStep to add validation
        goToStep(step) {
            // Validate that we can navigate to the requested step
            if (!this.canEditStep(step)) {
                return;
            }

            this.current_step = step;

            // Handle any step-specific logic
            switch (step) {
                case 2:
                    // Column mapping step - load sheet information
                    if (this.import_id && !this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;
                case 3:
                    // Preview step - start preview generation if needed
                    if (this.import_id && !this.preview_ready && !this.generating_preview && !this.preview_failed) {
                        this.startPreviewGeneration();
                    }
                    break;
                case 4:
                    // Completion step - no specific logic needed
                    break;
            }
        },

        // Override ConsolidatedImportMixin to handle contact-specific state transitions
        handleImportStateTransition() {
            // Handle contact-specific state transitions
            switch (this.import_state) {
                case 'uploading':
                    this.current_step = 1;
                    this.uploading = false;
                    this.upload_errors = ['Upload process was interrupted. Please try uploading the file again.'];
                    break;

                case 'uploaded':
                    this.current_step = 2;
                    if (!this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;


                case 'preview-ready':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'importing':
                    this.current_step = 4; // Execution step
                    this.importing = false;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    this.$rc.snackbar('Import execution was interrupted. Please retry from the preview step.', 'warning');
                    break;

                case 'completed':
                    this.current_step = 4;
                    this.importing = false;
                    break;

                case 'cancelled':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'failed':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    this.$rc.snackbar('Import failed. Please review the errors in the preview.', 'error');
                    break;
            }
        },

        // Override ConsolidatedImportMixin - Contact imports need specific navigation
        goBackToImportSearch() {
            this.goToImportHistory();
        },

        // Continue to preview (trigger column mapping save) - removed $refs usage
        onClickContinueToPreview() {
            this.onContinueToPreview();
        },


        onContinueToPreview() {
            // Trigger column mapping save directly
            this.saveColumnMapping();
        },

        // === COLUMN MAPPING API METHODS ===

        // Column mapping methods are now provided by ConsolidatedImportMixin

        onMappingFailed() {

            this.handleImportStateTransition();
            this.goToStep(3);
            this.preview_failed = true;
            this.preview_error_message = 'Import failed due to validation errors. Please review the errors below and fix your data.';
        },

        // Preview generation callbacks - ConsolidatedImportMixin handles most of this
        onPreviewComplete() {
            this.generating_preview = false;
            this.preview_ready = true;
            // Navigate to preview step
            this.current_step = 3;
        },

        // Import execution - use ConsolidatedImportMixin method
        onCompleteImport() {
            this.current_step = 4;
            this.onExecuteImport(); // Use mixin method
        },

        // Override ConsolidatedImportMixin execution completion
        onExecutionComplete() {
            this.importing = false;
            this.import_state = 'completed';
            this.current_step = 4;

            this.loadExistingImport();
        },

        onClickDownloadEnrichedDataFromPreview() {
            if (!this.import_meta_data || !this.import_meta_data.import_id) return;
            this.$rc.rest({
                spinner: true,
                endpoint: `GET: /api/v1/imports/${this.importType}/download-enriched-data/:organization_id/:import_id`,
                params: {
                    organization_id: this.$route.params.organization_id,
                    import_id: this.import_meta_data.import_id
                },
                on_success: (data) => {
                    window.open(data.download_url, '_blank');
                },
                on_error: 'Unable to download enriched data file'
            });
        },

        // Override ConsolidatedImportMixin execution failure
        onExecutionFailed(errorMessage) {
            this.importing = false;
            this.import_state = 'failed';
            this.execution_errors = [errorMessage || 'Execution failed unexpectedly'];
        },

        // Override ConsolidatedImportMixin execution request params to provide selected rows
        getExecutionRequestParams() {
            return {
                legal_attestation_specimen: '', // Contact imports don't require attestation, but backend expects this parameter
                selected_row_numbers: this.getSelectedRowNumbers() // Include only selected rows in the import
            };
        },

        // Column mapping callbacks
        onSheetChanged() {
            // Handle sheet change if needed
        },

        onMappingChanged() {
            // Handle mapping change if needed
        },

        onIntermediateCreated(data) {
            // Handle intermediate data creation
            this.intermediate_data = data;
        },


        // Override ConsolidatedImportMixin - Contact imports navigate to step 3 after mapping
        onMappingSaved() {
            this.handleImportStateTransition();
            this.goToStep(3);
        },



        // Navigation methods
        goToImportHistory() {
            // Check if user came from edit list view
            if (this.$route.query.return_to === 'edit-list' && this.$route.query.list_id) {
                this.$router.push({
                    name: 'edit-list',
                    params: {
                        list_id: this.$route.query.list_id
                    }
                });
                return;
            }

            // Use parent-provided route name; fallback to generic contact import search
            const route_name = (this.pageCopy && this.pageCopy.historyRouteName) || 'contact-import-search';
            this.$router.push({
                name: route_name,
                params: { organization_id: this.$route.params.organization_id }
            });
        },

        startNewImport() {
            this.$router.push({
                name: this.$route.name,
                params: { organization_id: this.$route.params.organization_id },
            });
            // Refresh the page without import ID to start a new import
            const add_to = this.import_meta_data && this.import_meta_data.add_import_result_to ? this.import_meta_data.add_import_result_to : null;
            const bulk_ids = add_to && Array.isArray(add_to.bulk_message_ids) && add_to.bulk_message_ids.length > 0 ? add_to.bulk_message_ids : null;
            if (bulk_ids) {
                const bulk_message_id = bulk_ids[0];
                this.$router.push({
                    query: {bulk_message_id}
                });
            }

            const list_ids = add_to && Array.isArray(add_to.list_ids) && add_to.list_ids.length > 0 ? add_to.list_ids : null;
            if (list_ids) {
                const list_id = list_ids[0];
                this.$router.push({
                    query: {list_id}
                });
            }

            //Reload the page to clear any state
            window.location.reload();
        },

        onClickDownloadTemplate() {
            // Use generic contact import template
            const download_url = this.$rc.getAPIEndpoint('/contact/download-import-template');
            window.open(download_url, '_blank');
        },

        // Override ConsolidatedImportMixin - Contact imports need confirmation dialog (similar to consent import)
        cancelImport() {
            this.$rc.confirm({
                title: 'Cancel Import',
                body: 'Are you sure you want to cancel this contact import? This action cannot be undone and the import will be marked as cancelled.',
                btn_text_ok: 'Yes, Cancel Import',
                btn_text_cancel: 'Keep Import',
                on_ok: () => {
                    this.$rc.rest({
                        'spinner': true,
                        'endpoint': `POST: /api/v1/imports/${this.importType}/cancel/${this.$route.params.organization_id}/${this.import_id}`,
                        'params': {},
                        'on_success': () => {
                            this.import_state = 'cancelled';
                            this.handleImportStateTransition();
                            this.$rc.snackbar('Import cancelled successfully', 'success');
                        },
                        'on_error': (error) => {
                            this.$rc.snackbar(`Failed to cancel import: ${error}`, 'error');
                        }
                    });
                }
            });
        },

        // Execute import with confirmation
        executeImport() {
            let body = 'Are you sure you want to perform this bulk contact import?';
            if (this.above_warn_change_contact_threshold) {
                body += '  <br><br>' + this.above_contact_threshold_warning_message;
            }
            // Preview data available for debugging if needed
            // Count the number of "include" state rows
            const includeCount = this.selected_rows.length;
            body += '  <br><br>Number of records selected to be imported: <b>' + includeCount + '</b>';

            // If user hasn't initiated validation, and there are unknown phone validation statuses in the selected rows, warn them
            if (!this.validating_phone_numbers && !this.phone_validation_batch_id) {
                const unknown_count = (this.selected_rows || []).filter(row => {
                    const status = ((row && row.rc_validation_status) || '').toString().trim().toLowerCase();
                    return status === 'unknown';
                }).length;
                if (unknown_count > 0) {
                    const plural = unknown_count === 1 ? '' : 's';
                    body += '  <br><br><b>Note:</b> ' + unknown_count + ' selected phone number' + plural + ' ha' + (unknown_count === 1 ? 's' : 've') + ' an <b>unknown</b> validation status. This means some numbers may not be valid. You can proceed, but you may want to click "Phone Options" → "Validate Unknown Numbers" first.';
                }
            }

            this.$rc.confirm({
                title: 'Execute Import',
                body,
                btn_text_ok: 'Yes, Execute Import',
                btn_text_cancel: 'Back to Preview',
                on_ok: () => {
                    this.onCompleteImport();
                }
            });
        },

        // Navigate to primary destination based on context
        goToPrimaryDestination() {
            // Prefer parent-provided navigation if present; otherwise use computed fallback
            if (this.primaryDestination && typeof this.primaryDestination.navigate === 'function') {
                this.primaryDestination.navigate();
                return;
            }
            // Fallback (should be rare since primaryDestination always defined)
            this.$router.push({
                name: 'fulltext-search-contacts',
                params: { organization_id: this.$route.params.organization_id }
            });
        },

        // Navigate to contact import history
        goToContactImportHistory() {
            const route_name = (this.pageCopy && this.pageCopy.historyRouteName) || 'contact-import-search';
            this.$router.push({
                name: route_name,
                params: { organization_id: this.$route.params.organization_id }
            });
        }
    }
};
</script>
